import { diaAr, horaAr } from "./fechaAr";

/* Qué se dice cuando alguien le pide al CENT la RTO de una unidad ahora.
 *
 * Lo pidió Igna el 09/10 a raíz del mail de Aldo (ticket #371): el furgón 448
 * había hecho la revisión al mediodía y el hub seguía con lo consultado a las
 * 04:35. La consulta la hace el bot (`services/rtoCent.js`); acá vive cómo se
 * cuenta lo que volvió, puro, para probarlo en una tabla.
 *
 * ── TRES RESPUESTAS QUE NO SE PUEDEN PARECER ─────────────────────────────
 *
 *   · entró una revisión nueva → se dice cuál y hasta cuándo vale, que es lo
 *     que vino a buscar el que apretó;
 *   · el CENT no tiene nada más nuevo → se dice CON LA HORA. Sin la hora,
 *     «no tiene una revisión más nueva» se lee igual que el dato viejo de la
 *     madrugada, que es justo la confusión que trajo esto. Y se dice qué
 *     hacer, con las mismas palabras que el bot por WhatsApp: si se hizo hoy,
 *     puede tardar en aparecer. No se dice «todavía no la publicó»: el botón
 *     está en todas las filas, y en una unidad que no fue a la planta no hay
 *     ninguna revisión que publicar;
 *   · no se pudo preguntar (el CENT caído, apretado hace un minuto) → lo dice
 *     el bot, que sabe por qué, y la fila sigue mostrando lo que había.
 */

/* El permiso que hace falta para ver la RTO en el hub, y por lo tanto para el
 * botón: la pantalla de RTO, la de acoplados y el bloque de la ficha cuelgan
 * de éste. El bot acepta además los otros con los que la base deja leer
 * `rto` (`ver.flota`, `ver.porteria`, `admin.permisos`), pero en el hub la RTO
 * no se dibuja sin `ver.documentos`: un botón donde no se ve el dato que
 * refresca sería una llave que no abre nada. */
export const PERMISO_CONSULTAR_CENT = "ver.documentos";

/* `null` es que los permisos todavía no llegaron, y ahí se muestra todo —la
 * misma regla que el rail y que el lápiz—. La pared de verdad es el bot. */
export function puedeConsultarCENT(permisos) {
  if (permisos === null || permisos === undefined) return true;
  return Array.isArray(permisos) && permisos.includes(PERMISO_CONSULTAR_CENT);
}

/* La patente como la entiende el CENT: ABC123 o AB123CD. La misma regla que
 * `rto_cent.normalizar` en el bot. */
export function limpiar(dominio) {
  const d = String(dominio || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  return /^([A-Z]{3}\d{3}|[A-Z]{2}\d{3}[A-Z]{2})$/.test(d) ? d : null;
}

/* ¿Hay algo que preguntarle al CENT por esta fila?
 *
 * `sin_patente` no: en el padrón tiene el interno copiado en el campo de la
 * patente, y preguntar por «448» sería gastar una consulta para que el CENT
 * diga que no existe. Lo que se ofrece ahí es cargar la patente, y eso ya lo
 * dice el veredicto. */
export function sePuedeConsultar(fila) {
  if (!fila || fila.estado === "sin_patente") return false;
  return !!limpiar(fila.dominio);
}

/* Lo que vuelve de `consultarCENT`, en una frase y un tono.
 *
 *   nueva   entró una revisión que no estaba
 *   igual   el CENT contestó y no tiene nada más nuevo
 *   espera  429: esa patente recién, el tope por hora o las otras que
 *           están esperando al CENT — el texto del bot dice cuál
 *   error   no se pudo (el CENT, la sesión, la red)
 */
export function mensajeDeConsulta(r) {
  if (!r) return null;
  if (r.ok) {
    if (r.novedad) {
      const e = r.estado || {};
      const revisado = e.revisado ? diaAr(e.revisado).slice(0, 5) : "";
      const vence = e.vence ? diaAr(e.vence) : "";
      let texto = revisado
        ? `Entró la revisión del ${revisado}`
        : "Entró una revisión nueva";
      texto += vence ? `: vence ${vence}.` : ".";
      /* Una Condicional también es una revisión que entró, y no vale lo mismo
       * que una Apta: dicha igual, el que la pidió da el camión por resuelto. */
      if (e.resultado && e.resultado !== "Apto") texto += ` Resultado: ${e.resultado}.`;
      return { tono: "nueva", texto };
    }
    const hora = horaAr(r.consultadoEn);
    return {
      tono: "igual",
      texto:
        "El CENT no tiene una revisión más nueva" +
        (hora ? ` (consultado ${hora}).` : ".") +
        " Si se hizo hoy, puede tardar en aparecer.",
    };
  }
  if (r.codigo === 429) {
    return {
      tono: "espera",
      texto: r.error || "Ahora no se puede consultar el CENT. Probá de nuevo en unos minutos.",
    };
  }
  return { tono: "error", texto: r.error || "No se pudo consultar. Probá de nuevo en un rato." };
}
